import React from 'react'
import numeral from 'numeral'
import { Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react'
import { Box, Flex, Spacer } from '@chakra-ui/layout'
import { useTranslation } from 'react-i18next'
import { Doughnut } from 'react-chartjs-2'
import type {
  AnalyticsAPIResponse,
  StatusAndErrorCodeDistributionPieChartData,
  ObjectOfNameAndCount,
} from '../../interfaces'
import DoughnutColuredLabel from './DoughnutColuredLabel'

const pieBackgrounds = [
  '#FB8C00',
  '#80DEEA',
  '#5E35B1',
  '#4CAF50',
  '#FF8A80',
  '#B0BEC5',
  '#795548',
  '#BF360C',
  '#C6FF00',
  '#006064',
  '#DCEDC8',
  '#B2EBF2',
  '#5C6BC0',
  '#6A1B9A',
  '#B71C1C',
  '#448AFF',
  '#64FFDA',
  '#AFB42B',
  '#FFD54F',
  '#FF6E40',
]

const getPieChartData = (
  data: ObjectOfNameAndCount[],
): StatusAndErrorCodeDistributionPieChartData | null => {
  if (!data.length) {
    return null
  }

  let total = 0
  data.forEach(element => {
    total += Number(element.count)
  })

  const labels: string[] = []
  const dataset: number[] = []
  for (const errorCodeData of data) {
    if (errorCodeData.count) {
      const percentage = parseFloat(
        ((errorCodeData.count / total) * 100).toFixed(2),
      )

      dataset.push(percentage)
      labels.push(errorCodeData.name)
    }
  }

  return {
    data: {
      datasets: [
        {
          backgroundColor: pieBackgrounds,
          data: dataset,
        },
      ],
      labels,
    },
    countData: data,
    options: {
      responsive: true,
      radius: 125,
      plugins: {
        legend: {
          display: false,
        },
      },
    },
  }
}

interface Props {
  data: AnalyticsAPIResponse
}

const ErrorCodeDistribution = ({ data }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const errorCodeData = data.errorCodeWise

  if (!errorCodeData || !errorCodeData.length) {
    return (
      <Box marginTop="1rem">{t('no_data_available', 'No Data Available')}</Box>
    )
  }

  const pieChartData = getPieChartData(errorCodeData)
  const tableRows: React.ReactElement[] = []

  if (pieChartData) {
    for (let i = 0; i < pieChartData.data.labels.length; i++) {
      tableRows.push(
        <Tr key={pieChartData.data.labels[i]}>
          <Td>
            <DoughnutColuredLabel
              label={pieChartData.data.labels[i]}
              color={pieChartData.data.datasets[0].backgroundColor[i]}
            />
          </Td>
          <Td>{`${pieChartData.data.datasets[0].data[i]} %`}</Td>
          <Td>
            {numeral(pieChartData.countData[i].count)
              .format('0.0 a')
              .toUpperCase()}
          </Td>
        </Tr>,
      )
    }
  }

  return (
    <Box marginTop="2.5rem">
      {errorCodeData.length && (
        <Flex alignItems="center">
          <Box>
            {pieChartData && (
              <Doughnut
                data={pieChartData.data}
                options={pieChartData.options}
              />
            )}
          </Box>
          <Spacer />
          <Box>
            <Table>
              <Thead>
                <Tr>
                  <Th>{t('error_code', 'Error Code')}</Th>
                  <Th>{t('percentage', 'Percentage')}</Th>
                  <Th>{t('requests', 'Requests')}</Th>
                </Tr>
              </Thead>
              <Tbody>{tableRows}</Tbody>
            </Table>
          </Box>
        </Flex>
      )}
      {(typeof errorCodeData === 'undefined' || !errorCodeData.length) && (
        <Box>{t('no_data_available', 'No Data Available')}</Box>
      )}
    </Box>
  )
}

export default ErrorCodeDistribution
